// Reopening an old draft: its choices are checked against the CURRENT configuration
// (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "Reopening an old
// draft"). Today the pickers silently show a different option while the stale id is sent,
// and a stale custom-field value is dropped without a word; this names each one so filing
// can wait until the agent has resolved or acknowledged it.
//
// Pure: `config` is whatever the create screen loaded.
//   config.loaded    false until the lists below have been fetched; nothing is reported before.
//   config.forms     `GET /api/ticket-forms` rows (`id`, `is_default`, `fields[].key`); the
//                    endpoint returns only ACTIVE forms, so a deleted or deactivated one is absent.
//   config.queues    `GET /api/queues` rows.
//   config.assignees `GET /api/users?can_be_assigned=true` rows.
//   config.location  the CURRENT row of the draft's store (`{ id, is_active }`), fetched by
//                    the screen; null/absent when it is not known, in which case nothing is
//                    reported. Retired means `is_active` is `false` or `0`: a row without
//                    the flag says nothing about it. The draft stores only the store's id, and `GET /:id` does not
//                    say whether the store is still active.
//
// Only a choice the agent MADE is checked (`touched`). An untouched form, queue or owner is
// absent from the saved draft and resolves at filing, so it cannot be stale.

import { DEFAULTABLE } from './draft-model.js';

function sameId(a, b) {
  return String(a) === String(b);
}

function hasValue(value) {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (value === false) return false;
  return true;
}

const CHOICES = [
  { field: 'form_id', list: 'forms', message: 'The form you chose is no longer available. Choose another.' },
  { field: 'queue_id', list: 'queues', message: 'The queue you chose is no longer available. Choose another.' },
  {
    field: 'assigned_user_id',
    list: 'assignees',
    message: 'The owner you chose can no longer be assigned tickets. Choose another, or leave it unassigned.',
  },
];

/**
 * @param {{ caller?: object, fields?: object }} draft  The screen's state (`fromDraft`'s
 *   `caller` and `fields`). The store lives in `caller`, the rest in `fields`.
 * @param {Iterable<string>} touched  Which DEFAULTABLE keys the agent chose.
 * @param {object} config  See the header.
 * @returns {{ field: string, message: string, blocking: boolean }[]}  Form, queue, owner,
 *   store, then custom fields. `blocking` findings must be resolved before filing; the
 *   custom-field ones only say a value will be dropped.
 */
export function findStaleChoices(draft, touched, config) {
  if (!config || config.loaded === false) return [];
  const fields = draft?.fields ?? {};
  const chosen = new Set(touched ?? []);
  const found = [];

  let formGone = false;
  for (const { field, list, message } of CHOICES) {
    if (!DEFAULTABLE.includes(field) || !chosen.has(field)) continue;
    const value = fields[field];
    // '' and null are choices ("none", "unassigned"), not references that can go stale.
    if (value === null || value === undefined || value === '') continue;
    if ((config[list] ?? []).some((row) => sameId(row.id, value))) continue;
    if (field === 'form_id') formGone = true;
    found.push({ field, message, blocking: true });
  }

  const location = draft?.caller?.location;
  if (location && config.location && sameId(config.location.id, location.id) && (config.location.is_active === false || config.location.is_active === 0)) {
    found.push({
      field: 'location',
      message: `Store ${location.name ?? location.id} has been retired. Choose another store.`,
      blocking: true,
    });
  }

  // The form the ticket will be filed with: the one chosen, else today's default.
  if (!formGone) {
    const forms = config.forms ?? [];
    const formId = chosen.has('form_id') && fields.form_id ? fields.form_id : null;
    const form = formId ? forms.find((row) => sameId(row.id, formId)) : forms.find((row) => row.is_default);
    if (form) {
      const keys = new Set((form.fields ?? []).map((field) => field.key));
      for (const [key, value] of Object.entries(fields.custom_fields ?? {})) {
        if (keys.has(key) || !hasValue(value)) continue;
        found.push({
          field: `custom_fields.${key}`,
          message: `Field '${key}' no longer exists on this form; its value will not be filed.`,
          blocking: false,
        });
      }
    }
  }

  return found;
}
